// Секциите на началния екран на „Доставка": стоковите без фактура (по
// доставчик: „Въведи фактура" и „Приключи без фактура" за избраните от него) и
// въведените днес (незавършената ми доставка и тези на колегите са общата
// `components/drafts/DraftHomeSection`). Плътни редове на един ред (36px, на
// пръст ≥44px), вторичното и бележката — приглушено на същия ред. Всяка секция
// е глупав рендер — данните идват от страницата.
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Delivery } from "../../../../../api/inventory";
import { formatMoney } from "../../../components/numbers";
import { SECONDARY_BUTTON_CLASS } from "../../../components/list/buttonStyles";
import { ROW_BUTTON, ROW_DENSE, ROW_HEIGHT } from "../../../components/list/density";
import { SectionCard, SectionTitle } from "../../../components/list/ListBits";
import { DeliveryPaymentPill, DeliveryTypeBadge } from "../DeliveryBadges";
import { deliveryGrossOf } from "../deliveryMeta";

// Стоковите без фактура, групирани по доставчик
export const UninvoicedNotesSection = ({
  notes,
  onInvoice,
  onCloseNotes,
  onViewNote,
}: {
  notes: Delivery[];
  onInvoice: (supplierId: number | null, notes: Delivery[]) => void;
  // „Приключи без фактура" за стоковите на доставчика (в модала — отметки)
  onCloseNotes?: (notes: Delivery[]) => void;
  onViewNote: (note: Delivery) => void;
}) => {
  const { t } = useTranslation();
  const groups = new Map<string, Delivery[]>();
  for (const note of notes) {
    const key = String(note.supplierId ?? "none");
    groups.set(key, [...(groups.get(key) ?? []), note]);
  }
  if (!groups.size) return null;
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle title={t("inventoryPage.homeUninvoicedTitle")} hint={t("inventoryPage.homeUninvoicedHint")} />
      <SectionCard>
        {[...groups.values()].map((group, index) => {
          const dates = group.map((note) => note.date).sort();
          const total = group.reduce((sum, note) => sum + deliveryGrossOf(note), 0);
          return (
            <div
              key={group[0].supplierId ?? "none"}
              className={`flex flex-col gap-2 px-[18px] py-2.5 ${index ? "border-t border-shade-1" : ""}`}
            >
              <div className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 ${ROW_HEIGHT}`}>
                <span className="min-w-0 shrink truncate font-semibold text-ink">
                  {group[0].supplierName || t("inventoryPage.homeNoSupplier")}
                </span>
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted">
                  {t("inventoryPage.homeNotesInfo", {
                    count: group.length,
                    from: dayjs(dates[0]).format("DD.MM"),
                    to: dayjs(dates[dates.length - 1]).format("DD.MM"),
                  })}
                </span>
                <span className="shrink-0 font-sheet font-semibold">{formatMoney(total)}</span>
                {onCloseNotes && (
                  <button type="button" onClick={() => onCloseNotes(group)} className={`${SECONDARY_BUTTON_CLASS} ${ROW_BUTTON}`}>
                    {t("inventoryPage.noteClose")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onInvoice(group[0].supplierId ?? null, group)}
                  className={`${SECONDARY_BUTTON_CLASS} ${ROW_BUTTON}`}
                >
                  {t("inventoryPage.homeEnterInvoice")}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {group.map((note) => (
                  <button
                    key={note.id}
                    type="button"
                    onClick={() => onViewNote(note)}
                    title={note.note ?? undefined}
                    className="flex min-h-8 max-w-full cursor-pointer items-center gap-[7px] rounded-lg border border-info/30 bg-info-tint px-[9px] py-1 text-[12.5px] text-ink hover:border-info pointer-coarse:min-h-11"
                  >
                    <DeliveryTypeBadge documentType="note" />
                    <span className="shrink-0">
                      {note.invoiceNumber || t("inventoryPage.noDocNumber")} · {dayjs(note.date).format("DD.MM")}
                    </span>
                    {note.note && <span className="min-w-0 max-w-[220px] truncate text-muted">{note.note}</span>}
                    <span className="shrink-0 font-sheet text-body">{formatMoney(deliveryGrossOf(note))}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </SectionCard>
    </section>
  );
};

// Въведените днес
export const TodayDeliveriesSection = ({
  deliveries,
  onView,
}: {
  deliveries: Delivery[];
  onView: (delivery: Delivery) => void;
}) => {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle
        title={t("inventoryPage.homeTodayTitle")}
        hint={t("inventoryPage.homeTodayCount", { count: deliveries.length })}
      />
      <SectionCard>
        {deliveries.map((delivery, index) => (
          <button
            key={delivery.id}
            type="button"
            onClick={() => onView(delivery)}
            className={`flex w-full cursor-pointer items-center gap-3 border-none bg-transparent px-[18px] text-left text-ink outline-none hover:bg-fill focus-visible:bg-fill ${ROW_DENSE} ${
              index ? "border-t border-solid border-shade-1" : ""
            }`}
          >
            <span className="w-11 shrink-0 font-sheet text-[13px] text-body">
              {dayjs(delivery.createdAt ?? delivery.date).format("HH:mm")}
            </span>
            <DeliveryTypeBadge documentType={delivery.documentType} />
            <span className="min-w-0 shrink truncate font-medium">
              {delivery.supplierName || t("inventoryPage.homeNoSupplier")}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted" title={delivery.note ?? undefined}>
              {[delivery.invoiceNumber || t("inventoryPage.noDocNumber"), delivery.createdByName ?? "—", delivery.note]
                .filter(Boolean)
                .join(" · ")}
            </span>
            <span className="shrink-0 font-sheet font-semibold">{formatMoney(deliveryGrossOf(delivery))}</span>
            <DeliveryPaymentPill delivery={delivery} />
          </button>
        ))}
        {deliveries.length === 0 && (
          <div className="px-[18px] py-4 text-muted">{t("inventoryPage.homeTodayNone")}</div>
        )}
      </SectionCard>
    </section>
  );
};
